import Konva from 'konva';

export type AnnotationTool = 'select' | 'freehand' | 'line' | 'arrow' | 'dimension' | 'rect' | 'ellipse' | 'text';

interface AnnotationShapeBase {
  color: string;
  strokeWidth: number;
}

export interface FreehandAnnotationShape extends AnnotationShapeBase {
  type: 'freehand';
  points: number[];
}

export interface SegmentAnnotationShape extends AnnotationShapeBase {
  type: 'line' | 'arrow' | 'dimension';
  points: [number, number, number, number];
  /** Nur bei `dimension`: das Mass, z. B. „3.20 m". */
  label?: string;
}

export interface BoxAnnotationShape extends AnnotationShapeBase {
  type: 'rect' | 'ellipse';
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TextAnnotationShape extends AnnotationShapeBase {
  type: 'text';
  x: number;
  y: number;
  text: string;
}

export type AnnotationShape = FreehandAnnotationShape | SegmentAnnotationShape | BoxAnnotationShape | TextAnnotationShape;

/**
 * Gespeicherte Zeichnung (`documents.annotation_json`). Alle Koordinaten und Strichstärken sind Pixel des Originalbilds;
 * `imageWidth`/`imageHeight` erlauben das Umrechnen, falls das Original später in anderer Grösse geladen wird.
 */
export interface ImageAnnotation {
  version: 1;
  imageWidth: number;
  imageHeight: number;
  shapes: AnnotationShape[];
}

export const ANNOTATION_COLORS = ['#e53935', '#fdd835', '#1e88e5', '#43a047', '#000000', '#ffffff'];

export const ANNOTATION_SIZE_FACTORS = [0.6, 1, 1.8];

const TEXT_SIZE_FACTOR = 6;

/** Strichstärke bei Grösse „M": wächst mit dem Bild, damit sie auf 4000 px wie auf 1000 px gleich wirkt. */
export function getBaseStrokeWidth(imageWidth: number, imageHeight: number): number {
  return Math.max(2, Math.round(Math.max(imageWidth, imageHeight) / 350));
}

export function parseImageAnnotation(json: string | null | undefined, imageWidth: number): AnnotationShape[] {
  if (!json) {
    return [];
  }
  try {
    const annotation = JSON.parse(json) as ImageAnnotation;
    const factor = annotation.imageWidth ? imageWidth / annotation.imageWidth : 1;
    if (Math.abs(factor - 1) < 0.001 || !Number.isFinite(factor)) {
      return annotation.shapes ?? [];
    }
    return (annotation.shapes ?? []).map((shape) => scaleAnnotationShape(shape, factor));
  } catch {
    return [];
  }
}

export function serializeImageAnnotation(shapes: AnnotationShape[], imageWidth: number, imageHeight: number): string {
  const annotation: ImageAnnotation = { version: 1, imageWidth, imageHeight, shapes };
  // Eine Nachkommastelle reicht bei Bildpixeln und hält das JSON klein (Freihand erzeugt viele Punkte).
  return JSON.stringify(annotation, (_, value: unknown) => (typeof value === 'number' ? Math.round(value * 10) / 10 : value));
}

export function translateAnnotationShape(shape: AnnotationShape, dx: number, dy: number): AnnotationShape {
  switch (shape.type) {
    case 'freehand':
      return { ...shape, points: shape.points.map((value, index) => value + (index % 2 === 0 ? dx : dy)) };
    case 'line':
    case 'arrow':
    case 'dimension': {
      const [x1, y1, x2, y2] = shape.points;
      return { ...shape, points: [x1 + dx, y1 + dy, x2 + dx, y2 + dy] };
    }
    case 'rect':
    case 'ellipse':
    case 'text':
      return { ...shape, x: shape.x + dx, y: shape.y + dy };
  }
}

function scaleAnnotationShape(shape: AnnotationShape, factor: number): AnnotationShape {
  const strokeWidth = shape.strokeWidth * factor;
  switch (shape.type) {
    case 'freehand':
      return { ...shape, strokeWidth, points: shape.points.map((value) => value * factor) };
    case 'line':
    case 'arrow':
    case 'dimension': {
      const [x1, y1, x2, y2] = shape.points;
      return { ...shape, strokeWidth, points: [x1 * factor, y1 * factor, x2 * factor, y2 * factor] };
    }
    case 'rect':
    case 'ellipse':
      return {
        ...shape,
        strokeWidth,
        x: shape.x * factor,
        y: shape.y * factor,
        width: shape.width * factor,
        height: shape.height * factor,
      };
    case 'text':
      return { ...shape, strokeWidth, x: shape.x * factor, y: shape.y * factor };
  }
}

/** Ist die Form gross genug, um sie zu behalten? Verhindert Punkte und Striche durch versehentliches Tippen. */
export function isAnnotationShapeMeaningful(shape: AnnotationShape): boolean {
  const minimum = shape.strokeWidth * 2;
  switch (shape.type) {
    case 'freehand':
      return shape.points.length >= 4;
    case 'line':
    case 'arrow':
    case 'dimension': {
      const [x1, y1, x2, y2] = shape.points;
      return Math.hypot(x2 - x1, y2 - y1) >= minimum;
    }
    case 'rect':
    case 'ellipse':
      return Math.abs(shape.width) >= minimum && Math.abs(shape.height) >= minimum;
    case 'text':
      return shape.text.trim().length > 0;
  }
}

/**
 * Baut die Konva-Darstellung einer Form. Wird für die Anzeige und für den Export in Originalgrösse gleich verwendet.
 * `minHitStrokeWidth` (in Bildpixeln) hält dünne Linien im Editor mit dem Finger greifbar, egal wie weit herausgezoomt ist.
 */
export function buildAnnotationNode(shape: AnnotationShape, minHitStrokeWidth = 0): Konva.Group {
  const group = new Konva.Group();
  const hitStrokeWidth = Math.max(shape.strokeWidth * 4, minHitStrokeWidth);
  switch (shape.type) {
    case 'freehand':
      group.add(
        new Konva.Line({
          points: shape.points,
          stroke: shape.color,
          strokeWidth: shape.strokeWidth,
          lineCap: 'round',
          lineJoin: 'round',
          tension: 0.4,
          hitStrokeWidth,
        }),
      );
      break;
    case 'line':
      group.add(
        new Konva.Line({ points: shape.points, stroke: shape.color, strokeWidth: shape.strokeWidth, lineCap: 'round', hitStrokeWidth }),
      );
      break;
    case 'arrow':
      group.add(
        new Konva.Arrow({
          points: shape.points,
          stroke: shape.color,
          fill: shape.color,
          strokeWidth: shape.strokeWidth,
          pointerLength: shape.strokeWidth * 4,
          pointerWidth: shape.strokeWidth * 4,
          lineCap: 'round',
          hitStrokeWidth,
        }),
      );
      break;
    case 'dimension':
      addDimensionNodes(group, shape, hitStrokeWidth);
      break;
    case 'rect':
      group.add(
        new Konva.Rect({
          x: Math.min(shape.x, shape.x + shape.width),
          y: Math.min(shape.y, shape.y + shape.height),
          width: Math.abs(shape.width),
          height: Math.abs(shape.height),
          stroke: shape.color,
          strokeWidth: shape.strokeWidth,
          hitStrokeWidth,
        }),
      );
      break;
    case 'ellipse':
      group.add(
        new Konva.Ellipse({
          x: shape.x + shape.width / 2,
          y: shape.y + shape.height / 2,
          radiusX: Math.abs(shape.width / 2),
          radiusY: Math.abs(shape.height / 2),
          stroke: shape.color,
          strokeWidth: shape.strokeWidth,
          hitStrokeWidth,
        }),
      );
      break;
    case 'text': {
      const label = buildTextLabel(shape.text, shape.color, shape.strokeWidth * TEXT_SIZE_FACTOR);
      label.position({ x: shape.x, y: shape.y });
      group.add(label);
      break;
    }
  }
  return group;
}

function addDimensionNodes(group: Konva.Group, shape: SegmentAnnotationShape, hitStrokeWidth: number) {
  const [x1, y1, x2, y2] = shape.points;
  const length = Math.hypot(x2 - x1, y2 - y1) || 1;
  // Normale auf die Masslinie, für die Endstriche.
  const nx = -(y2 - y1) / length;
  const ny = (x2 - x1) / length;
  const tick = shape.strokeWidth * 5;
  const common = { stroke: shape.color, strokeWidth: shape.strokeWidth, lineCap: 'round' as const, hitStrokeWidth };

  group.add(
    new Konva.Arrow({
      ...common,
      points: shape.points,
      fill: shape.color,
      pointerLength: shape.strokeWidth * 4,
      pointerWidth: shape.strokeWidth * 4,
      pointerAtBeginning: true,
      pointerAtEnding: true,
    }),
  );
  group.add(new Konva.Line({ ...common, points: [x1 - nx * tick, y1 - ny * tick, x1 + nx * tick, y1 + ny * tick] }));
  group.add(new Konva.Line({ ...common, points: [x2 - nx * tick, y2 - ny * tick, x2 + nx * tick, y2 + ny * tick] }));

  if (shape.label?.trim()) {
    const fontSize = shape.strokeWidth * TEXT_SIZE_FACTOR;
    const label = buildTextLabel(shape.label.trim(), shape.color, fontSize);
    // Text immer lesbar halten: nie kopfüber, und auf der Seite, die für den Text „oben" ist.
    let angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
    if (angle > 90) {
      angle -= 180;
    } else if (angle < -90) {
      angle += 180;
    }
    const radians = (angle * Math.PI) / 180;
    const offset = fontSize * 0.9;
    label.position({ x: (x1 + x2) / 2 + Math.sin(radians) * offset, y: (y1 + y2) / 2 - Math.cos(radians) * offset });
    label.offsetX(label.width() / 2);
    label.offsetY(label.height() / 2);
    label.rotation(angle);
    group.add(label);
  }
}

function buildTextLabel(text: string, color: string, fontSize: number): Konva.Label {
  const isLightColor = color.toLowerCase() === '#ffffff' || color.toLowerCase() === '#fdd835';
  const label = new Konva.Label();
  label.add(
    new Konva.Tag({
      fill: isLightColor ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
      cornerRadius: fontSize * 0.2,
    }),
  );
  label.add(
    new Konva.Text({
      text,
      fontSize,
      fontStyle: 'bold',
      fontFamily: 'Arial, Helvetica, sans-serif',
      fill: color,
      padding: fontSize * 0.25,
    }),
  );
  return label;
}
